import clsx from 'clsx'
import { Form, Formik } from 'formik'
import React, { FC, ReactNode, useContext, useRef, useState } from 'react'

import Container from '@Components/CMS/Container'
import { Title } from '@Components/CMS/Title'
import { TextField } from '@Components/FormElements/Formik/TextField'
import { RTEContent } from '@Components/RTEContent'
import { Button } from '@CoreComponents/Buttons/Button'
import { MetadataContext } from '@Globals/Metadata'
import { useNewsletterSubscription } from '@Globals/Newsletter/useNewsletterSubscription'
import { useTranslation } from '@Globals/Translations'
import { EmailSuggestion } from '@Shared/Components/Suggestions/EmailSuggestion/EmailSuggestion'

import styles from './SubscribeNewsletterForm.module.scss'

const UnsubscribePolicy = () => {
  const { t, tDanger } = useTranslation()
  const { cmsData } = useContext(MetadataContext)

  return (
    <p className={styles.unsubscribe}>
      <span
        {...tDanger(
          'footer:I_AM_AWARE_OF_TRENDHIM_PRIVACY_POLICY',
          {
            arg1: cmsData?.urls?.privacyPolicy?.url,
          },
        )}
      />
      .
      {' '}
      {t('footer:UNSUBSCRIBE_AT_ANY_TIME')}
      .
    </p>
  )
}

interface SubscribeNewsletterFormProps {
  className?: string
  description?: string
  id?: string
  onSubscribe: (email: string) => void
  policy?: ReactNode
  submitText: string
  title?: string
  variant: 'column' | 'row'
}

export const SubscribeNewsletterForm: FC<SubscribeNewsletterFormProps> = ({
  className,
  description,
  id,
  onSubscribe,
  policy,
  submitText,
  title,
  variant,
}) => {
  const { t } = useTranslation()
  const formikRef = useRef<any>(null)
  const { isSuggestion, newsletterSignupSchema }
      = useNewsletterSubscription(formikRef)
  const [emailState, setEmailState] = useState<string>('')

  const wrapperClass = clsx({
    [styles.column]: variant === 'column',
    [styles.row]: variant === 'row',
    'error-is-suggestion': isSuggestion,
  })

  return (
    <Formik
      initialValues={{ email: '' }}
      innerRef={formikRef}
      onSubmit={async ({ email }) => onSubscribe(email)}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={newsletterSignupSchema}
    >
      <div className={className}>
        {(title || description) && (
          <Container>
            {title && <Title title={title} type="h2" />}
            {description && <RTEContent html={description} />}
          </Container>
        )}
        {/* @ts-expect-error - TODO: remove formik in its entirety */}
        <Form className={styles.wrapper} id={id} noValidate>
          <EmailSuggestion
            checkForID="th-notify-me-newsletter-email"
            className={styles.emailSuggestion}
            email={emailState}
            onClick={suggestedEmail => formikRef.current.setValues({ email: suggestedEmail })}
          />
          <TextField
            id="th-notify-me-newsletter-email"
            label={t('footer:FOOTER_SIGN_UP_YOUR_EMAIL')}
            name="email"
            onInput={e => setEmailState((e as any)?.target?.value || '')}
            type="email"
            wrapperClassName={wrapperClass}
          />
          <div className={wrapperClass}>
            <Button fillWidth innerSpacing="medium" theme="black" type="submit">{submitText}</Button>
          </div>
          {policy || <UnsubscribePolicy />}
        </Form>
      </div>
    </Formik>
  )
}
